import React, { useContext, useState } from 'react';

import { type Dispatch, compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import withAccessControl from 'common/routing/withAccessControl';
import ButtonV2 from 'common/ui/ButtonV2';
import SingleSelect from 'common/ui/SingleSelectWithSearch';
import SwitchV2 from 'common/ui/SwitchV2';
import { H1, H2, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';

import AdminSettingsHeader from './AdminSettings/AdminSettingsHeader';

import type { Company, Member } from 'common/api/endpoints/companies';
import type { Option } from 'common/ui/common/select/SelectCommon';

import 'css/components/subdomain/admin/_AdminQueueSettings.scss';

const mapMembersToOptions = (members: Member[]) =>
  members.map((member) => ({
    avatar: member.avatarURL,
    label: member.name,
    value: member._id,
  }));

const initialSelectedAuthor = (memberOptions: Option[], company: Company) => () =>
  memberOptions.find((option) => option.value === company.autoDeduplication?.authorID);

type OwnProps = Record<string, never>;

type ConnectProps = {
  reloadCompany: () => Promise<void>;
};

type Props = OwnProps & ConnectProps;

const AdminQueueSettings = ({ reloadCompany }: Props) => {
  const company = useContext<Company>(CompanyContext);
  const showToast = useContext(ShowToastContext);

  const memberOptions = mapMembersToOptions(company.members);

  const [enabled, setEnabled] = useState(!!company.autoDeduplication?.enabled);
  const [saving, setSaving] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState(
    initialSelectedAuthor(memberOptions, company)
  );

  const save = async () => {
    setSaving(true);

    if (!selectedAuthor?.value) {
      showToast('Please select an author.', ToastTypes.error);
      setSaving(false);
      return;
    }

    const response = await AJAX.post('/api/queue/toggleAutomaticDedupe', {
      enabled,
      authorID: selectedAuthor.value,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      showToast(error.message, ToastTypes.error);
      setSaving(false);
      return;
    }

    showToast('Inbox settings updated successfully.', ToastTypes.success);

    await reloadCompany();
    setSaving(false);
  };

  const valuesChanged =
    company.autoDeduplication?.enabled !== enabled ||
    company.autoDeduplication?.authorID !== selectedAuthor?.value;

  const saveDisabled = !selectedAuthor?.value || !valuesChanged;

  return (
    <>
      <AdminSettingsHeader
        title="Inbox"
        subheading={<p>Enable automatic merging when new requests have a detected duplicate.</p>}
        learnMoreLink="https://help.canny.io/en/articles/8646863-automatic-post-deduplication"
      />
      <Helmet title={`Inbox Settings | Canny`} />
      <div className="adminQueueSettings">
        <div className="queueToggleSection">
          <div className="queueDetails">
            <H1 variant="headingMd">Automate deduplication</H1>
            <P className="queueBody" variant="bodyMd">
              Requests with a detected duplicate will be automatically merged into the existing
              post.
            </P>
          </div>
          <SwitchV2
            checked={enabled}
            onChange={() => setEnabled((value) => !value)}
            size="medium"
          />
        </div>
        <div className="queueAuthorSection">
          <div className="queueDetails">
            <H2 variant="headingSm">Creator</H2>
            <P className="queueBody" variant="bodyMd">
              The admin selected will be featured as the creator for votes-on-behalf or merges
              created by automated deduplication.
            </P>
          </div>
          <div className="queueAuthorSelect">
            <SingleSelect
              disabled={!enabled}
              labelPlacement="left"
              onChange={setSelectedAuthor}
              options={memberOptions}
              placeholder="Select Author"
              value={selectedAuthor}
            />
          </div>
        </div>
        <ButtonV2
          className="saveButton"
          disabled={saveDisabled}
          onClick={save}
          loading={saving}
          size="medium">
          Save changes
        </ButtonV2>
      </div>
    </>
  );
};

// TODO: remove cast once `connect` is typed
export default compose(
  connect(null, (dispatch: Dispatch<any>) => ({
    reloadCompany: () => {
      dispatch(reloadCompany());
    },
  })),
  withAccessControl<Props>(
    testEveryPermission(RoutePermissions.adminSettings.inbox),
    '/admin/settings'
  )
)(AdminQueueSettings) as unknown as React.FC<OwnProps>;
